import { useState, useCallback, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { EnrollmentsListTable } from "../components/enrollments/EnrollmentsListTable";
import { AddEnrollmentDialog } from "../components/enrollments/AddEnrollmentDialog";
import { EnrollmentFiltersDialog } from "../components/enrollments/EnrollmentFiltersDialog";
import { EditEnrollmentStatusDialog } from "../components/enrollments/EditEnrollmentStatusDialog";
import { ExportEnrollmentsDialog } from "../components/enrollments/ExportEnrollmentsDialog";
import { UpdateStatusDialog } from "../components/enrollments/UpdateStatusDialog";
import { enrollmentService } from "../services/enrollmentService";
import { courseService } from "../services/courseService";
import { Input } from "../components/ui/Input";
import { Search, Filter, Download } from "lucide-react";
import { Button } from "../components/ui/Button";
import { PermissionDenied } from "../components/ui/PermissionDenied";
import { useAuth } from "../contexts/AuthContext";
import { useServerPagination } from "@hooks/useServerPagination";
import { useDebounce } from "@hooks/useDebounce";

export default function EnrollmentsPage() {
  const { hasPermission } = useAuth();

  if (!hasPermission('enrollments:read')) {
    return <PermissionDenied requiredPermission="enrollments:read" />;
  }

  const canExport = hasPermission('analytics:export');

  const [courses, setCourses] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);

  const [selectedEnrollments, setSelectedEnrollments] = useState<Set<string>>(new Set());

  const [isUpdateStatusOpen, setIsUpdateStatusOpen] = useState(false);
  const [isBulkSuspendOpen, setIsBulkSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");

  const [filters, setFilters] = useState({
    status: "all",
    type: "all",
    courseId: "",
    minWatchTime: "",
    maxWatchTime: "",
    sortBandwidth: "none",
    paymentMethod: "all",
  });

  // Dialog state
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [editingEnrollment, setEditingEnrollment] = useState<any>(null);

  // Server-side pagination with useServerPagination hook
  const {
    data: enrollments,
    isLoading: isEnrollmentsLoading,
    page,
    totalPages,
    total: totalItems,
    setPage,
    refresh: fetchEnrollments,
  } = useServerPagination<any>({
    defaultLimit: 20,
    fetchData: useCallback(async ({ page, limit }) => {
      const params: any = { page, limit };

      // Add filters
      if (filters.status !== "all") params.status = filters.status;
      if (filters.type !== "all") params.isFree = filters.type === "free";
      if (filters.courseId) params.courseId = filters.courseId;
      if (filters.minWatchTime) params.minWatchTime = parseFloat(filters.minWatchTime) * 3600;
      if (filters.maxWatchTime) params.maxWatchTime = parseFloat(filters.maxWatchTime) * 3600;
      if (filters.sortBandwidth && filters.sortBandwidth !== "none") {
        params.sort = `watchTime:${filters.sortBandwidth}`;
      }
      if (filters.paymentMethod && filters.paymentMethod !== "all") {
        params.paymentMethod = filters.paymentMethod;
      }

      // Add search
      if (debouncedSearch) params.search = debouncedSearch;

      const response = await enrollmentService.getAllEnrollments(params);
      return {
        data: response.enrollments || [],
        total: response.total || 0,
        totalPages: response.totalPages || 1,
      };
    }, [filters, debouncedSearch]),
    deps: [filters, debouncedSearch],
  });

  // Fetch courses for filter dropdown
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const coursesRes = await courseService.getAllCourses({ limit: 1000 });
        // @ts-ignore
        const allCourses = coursesRes.data?.courses || coursesRes.courses || [];
        setCourses(allCourses);
      } catch (err) {
        console.error("Failed to load initial data:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleEdit = (item: any) => {
    setEditingEnrollment(item);
  };

  const handleDelete = async (item: any) => {
    if (
      confirm(
        "Are you sure you want to delete this enrollment? This cannot be undone."
      )
    ) {
      try {
        await enrollmentService.deleteEnrollment(item._id);
        fetchEnrollments(); // Reload
      } catch (err) {
        alert("Failed to delete enrollment");
      }
    }
  };

  const toggleSelection = (id: string, selected: boolean) => {
    setSelectedEnrollments((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const selectAllVisible = () => {
    const visibleIds = new Set(enrollments.map((e) => e._id));
    setSelectedEnrollments((prev) => {
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const deselectAllVisible = () => {
    const visibleIds = new Set(enrollments.map((e) => e._id));
    setSelectedEnrollments((prev) => {
      const next = new Set(prev);
      visibleIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedEnrollments(new Set());
  };

  const handleBulkSuspend = async () => {
    if (!suspendReason.trim()) {
      alert("Please provide a reason for suspension");
      return;
    }

    const ids = Array.from(selectedEnrollments);
    try {
      await enrollmentService.bulkSuspendEnrollments(ids, suspendReason);
      alert(`Successfully suspended ${ids.length} enrollment(s)`);
      setIsBulkSuspendOpen(false);
      setSuspendReason("");
      clearSelection();
      fetchEnrollments();
    } catch (err) {
      alert("Failed to suspend enrollments");
    }
  };

  const handleBulkDelete = async () => {
    if (
      !confirm(
        `Are you sure you want to delete ${selectedEnrollments.size} enrollment(s)? This cannot be undone.`
      )
    ) {
      return;
    }

    const ids = Array.from(selectedEnrollments);
    try {
      await enrollmentService.bulkDeleteEnrollments(ids);
      alert(`Successfully deleted ${ids.length} enrollment(s)`);
      clearSelection();
      fetchEnrollments();
    } catch (err) {
      alert("Failed to delete enrollments");
    }
  };

  const allVisibleSelected =
    enrollments.length > 0 &&
    enrollments.every((e) => selectedEnrollments.has(e._id));

  const someVisibleSelected =
    enrollments.some((e) => selectedEnrollments.has(e._id)) && !allVisibleSelected;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">
            Enrollments
          </h2>
          <p className="text-gray-500">
            Track student enrollments and progress.
          </p>
        </div>

        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
          {/* Search input */}
          <div className="w-full xl:w-[400px]">
            <Input
              placeholder="Search by student or course..."
              icon={<Search size={20} />}
              className="bg-white w-full"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
            />
          </div>

          {/* Filter and Primary Action */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">
            {/* Unified Filter Button */}
            <Button
              variant="outline"
              size="sm"
              className={`h-[44px] sm:h-[44px] px-4 gap-2 justify-center w-full sm:w-auto ${
                Object.values(filters).some((v) => v !== "all" && v !== "")
                  ? "border-[#102040] text-[#102040] bg-blue-50"
                  : ""
              }`}
              onClick={() => setIsFilterOpen(true)}
            >
              <Filter size={16} />
              <span>Filters</span>
              {Object.values(filters).filter((v) => v !== "all" && v !== "")
                .length > 0 && (
                <span className="flex items-center justify-center w-5 h-5 ml-1 text-[10px] font-bold text-white bg-[#102040] rounded-full">
                  {
                    Object.values(filters).filter(
                      (v) => v !== "all" && v !== ""
                    ).length
                  }
                </span>
              )}
            </Button>

            {/* Export button */}
            {canExport && (
              <Button
                variant="outline"
                size="lg"
                className="whitespace-nowrap gap-2 w-full sm:w-auto h-[48px] sm:h-[44px] justify-center"
                onClick={() => setIsExportOpen(true)}
              >
                <Download size={16} />
                Export CSV
              </Button>
            )}

            {/* Primary button */}
            <Button
              variant="primary"
              size="lg"
              className="whitespace-nowrap w-full sm:w-auto h-[48px] sm:h-[44px] justify-center"
              onClick={() => setIsAddOpen(true)}
            >
              Add Enrollment
            </Button>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedEnrollments.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-blue-800">
                {selectedEnrollments.size} enrollment(s) selected
              </span>
              <button
                onClick={clearSelection}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Clear selection
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {hasPermission('enrollments:update') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsUpdateStatusOpen(true)}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  Update Status
                </Button>
              )}
              {hasPermission('enrollments:update') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsBulkSuspendOpen(true)}
                  className="border-orange-300 text-orange-700 hover:bg-orange-100"
                >
                  Suspend Selected
                </Button>
              )}
              {hasPermission('enrollments:delete') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="border-red-300 text-red-700 hover:bg-red-100"
                >
                  Delete Selected
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Table wrapped in Card */}
        <EnrollmentsListTable
          enrollments={enrollments}
          isLoading={isLoading || isEnrollmentsLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          totalItems={totalItems}
          currentPage={page}
          pageSize={20}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          selectedIds={selectedEnrollments}
          onSelectChange={toggleSelection}
          onSelectAll={(selected) => (selected ? selectAllVisible() : deselectAllVisible())}
          allVisibleSelected={allVisibleSelected}
          someVisibleSelected={someVisibleSelected}
        />

        {/* Filters Dialog */}
        <EnrollmentFiltersDialog
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={filters}
          onApply={(newFilters) => {
            setFilters(newFilters);
          }}
          courses={courses.map((c) => ({ _id: c._id, title: c.title }))}
        />

        {/* Create Dialog */}
        <AddEnrollmentDialog
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
          onSuccess={() => {
            fetchEnrollments(); // Reload data
          }}
        />

        {/* Edit Status Dialog */}
        <EditEnrollmentStatusDialog
          isOpen={!!editingEnrollment}
          onClose={() => setEditingEnrollment(null)}
          onSuccess={() => {
            fetchEnrollments();
          }}
          enrollment={editingEnrollment}
        />

        {/* Export Dialog */}
        <ExportEnrollmentsDialog
          isOpen={isExportOpen}
          onClose={() => setIsExportOpen(false)}
          courses={courses.map((c) => ({ _id: c._id, title: c.title }))}
          initialFilters={{
            status: filters.status,
            type: filters.type,
            courseId: filters.courseId,
            minWatchTime: filters.minWatchTime,
            maxWatchTime: filters.maxWatchTime,
            paymentMethod:
              filters.paymentMethod && filters.paymentMethod !== "all"
                ? filters.paymentMethod
                : "",
          }}
          initialSearchTerm={searchTerm}
        />

        {/* Bulk Update Status Dialog */}
        <UpdateStatusDialog
          isOpen={isUpdateStatusOpen}
          onClose={() => setIsUpdateStatusOpen(false)}
          enrollmentIds={Array.from(selectedEnrollments)}
          onSuccess={() => {
            clearSelection();
            fetchEnrollments();
          }}
        />

        {/* Bulk Suspend Dialog */}
        {isBulkSuspendOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Suspend {selectedEnrollments.size} Enrollment(s)
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Suspension
                  </label>
                  <textarea
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    rows={3}
                    placeholder="Enter the reason for suspending these enrollments..."
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsBulkSuspendOpen(false);
                      setSuspendReason("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleBulkSuspend}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Suspend
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}